import { createParser } from "eventsource-parser";

import type { Message } from './App';
import { apiClient, getAuthHeader } from './apiClient';
import { retrieveAccessTokenFromBackend } from './security';

async function getMessagesWithTokenRefreshThrowOnError(chatId: string, content: string, file?: File | null) : Promise<Response> {
    const url = `${import.meta.env.VITE_API_URL}/api/chats/${chatId}/messages/`;

    // Helper to perform the POST with given headers
    async function fetchWithCredentials(headers: HeadersInit): Promise<Response> {
        const finalHeaders = { ...headers } as Record<string, string>;
        let body: BodyInit;

        if (file) {
            const formData = new FormData();
            formData.append('content', content);
            formData.append('files', file);
            formData.append('tool_calls', JSON.stringify([]));
            formData.append('tool_call_id', '');
            
            body = formData;
            // Omit Content-Type to let the browser set the multipart boundary
            delete finalHeaders['Content-Type'];
        } else {
            body = JSON.stringify({ content, tool_calls: [], tool_call_id: '' });
        }

        return fetch(url, {
            method: 'POST',
            headers: finalHeaders,
            credentials: 'include',
            body,
        });
    }

    // First attempt with the current auth header (may use a cached token)
    const initialHeaders: HeadersInit = {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
    };

    let response = await fetchWithCredentials(initialHeaders);
    if (response.status === 401) {
        try {
            const freshToken = await retrieveAccessTokenFromBackend();

            if (freshToken) {
                const retryHeaders: HeadersInit = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${freshToken}`,
                };

                response = await fetchWithCredentials(retryHeaders);
            }
        } catch {
            // If refreshing fails, we'll fall through to the !ok check below
        }
    }

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
        throw new Error('Response body is null');
    }

    return response;
}


export async function getMessagesForChat(chatId: string): Promise<Message[]> {
    const response = await apiClient.get(`/api/chats/${chatId}/messages/`);
    return response.data;
}

export async function createMessage(chatId: string, content: string): Promise<Message> {
    const response = await apiClient.post(
        `/api/chats/${chatId}/messages/`,
        { content, tool_calls: [], tool_call_id: '' },
    );
    return response.data;
}

export async function createMessageThenStreamResponse(
    chatId: string,
    content: string,
    onMessagesUpdated: (messages: Message[]) => void,
    file?: File | null
): Promise<void> {

    const parser = createParser({
        onEvent(ev) {
            const data = ev.data;
            const jsonData = JSON.parse(data);

            switch (ev.event) {
                case 'messages_update':
                    onMessagesUpdated(jsonData);
                    break;
                case 'error':
                    console.error('Error:', jsonData.message);
                    throw new Error(jsonData.message);
            }
        }
    });

    const response = await getMessagesWithTokenRefreshThrowOnError(chatId, content, file);
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }

            const decoded = decoder.decode(value, { stream: true });
            parser.feed(decoded);
        }
    } finally {
        // Cancel the reader to stop consuming the stream
        reader.cancel();
    }
}

export async function deleteChat(chatId: string): Promise<void> {
    await apiClient.delete(`/api/chats/${chatId}/`);
}

export async function renameChat(chatId: string, name: string): Promise<void> {
    await apiClient.patch(`/api/chats/${chatId}/`, { name });
}

export async function moveChatToProject(chatId: string, projectId: string | null): Promise<void> {
    await apiClient.patch(`/api/chats/${chatId}/`, { project: projectId || '' });
}
